import React, { FC, memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Input, Select, Radio, Button, Upload, Spin } from "antd";
import { AutoModel, TargetLanguageSelector, UploadProgressOverlay, VideoShow } from "../../components/common";
import { getLanguageCodeFromName, getSourceLanguageOptions } from "../../utils/languageUtils";
import { getDictDataListSimpleApi } from "../../service/dict";
import { MAX_UPLOAD_VIDEO_EPISODES } from "../../config/constants";
import { getVideoMetadata } from "../../utils/videoMetadata";
import { getVideoUploadBaseName, getVideoUploadFileNameInvalidMessage, validateVideoUploadFileName } from "../../utils/videoFileName";
import { uploadFileToTosApi } from "../../service/myWork";
import {
  confirmFastTranslateUploadApi,
  createFastTranslateSeriesApi,
  deleteFastTranslateUploadBatchApi,
  deleteFastTranslateUploadItemApi,
  getFastTranslatePresignedUrlsApi,
} from "../../service/fastTranslateSeries";
import { postTaskPointPreviewApi } from "../../service/task";
import { CoinsEstimateIcon } from "../../components/common/CoinsEstimateIcon";
import { triggerMembershipRefresh } from "../../service/membership";
import { useHasPermission } from "../../hooks/useHasPermission";
import { PERMISSIONS } from "../../constants/permissionKeys";
import "./QuickTrans.scss";

const QUICK_TRANS_UPLOAD_CONCURRENCY = 2;
const UPLOAD_PROGRESS_UI_MS = 100;
const QUICK_TRANS_POINTS_REFRESH_POLL_MS = 2000;
const QUICK_TRANS_POINTS_REFRESH_TOTAL_MS = 10000;

const yieldToPaint = () =>
  new Promise<void>((resolve) => {
    if (typeof document !== "undefined" && document.hidden) {
      queueMicrotask(() => resolve());
      return;
    }
    requestAnimationFrame(() => resolve());
  });

type UploadStatus = "queued" | "preparing" | "uploading" | "confirming" | "done" | "error";

interface QuickTransUploadItem {
  id: string;
  sourceFileName: string;
  file: File | null;
  previewUrl?: string;
  coverPreviewUrl?: string;
  status: UploadStatus;
  progress: number;
  error?: string;
  duration?: number;
  coverFileName?: string;
  sourcePath?: string;
  coverPath?: string;
  sourceSignature?: FastTranslateSeries.PresignedUrlSignature;
  coverSignature?: FastTranslateSeries.PresignedUrlSignature;
  episodeName?: string;
  episodeNo?: number;
  tosUrl?: string;
  tosKey?: string;
  coverUrl?: string;
}

interface QuickTransUploadGridCellProps {
  item: QuickTransUploadItem;
  selected: boolean;
  onSelect: () => void;
  onRemove: () => void;
}

const QuickTransUploadGridCell = memo(
  function QuickTransUploadGridCell({ item, selected, onSelect, onRemove }: QuickTransUploadGridCellProps) {
    const st = item.status ?? "queued";
    const thumbSrc = item.coverPreviewUrl || item.coverUrl;
    const cellOverlay =
      st === "done" ? null : st === "error" ? (
        <UploadProgressOverlay muted message={item.error || "失败"} showBar={false} />
      ) : st === "uploading" || st === "confirming" ? (
        <UploadProgressOverlay percent={item.progress || 0} />
      ) : st === "preparing" ? (
        <UploadProgressOverlay muted message="准备中" showBar={false} />
      ) : (
        <UploadProgressOverlay muted message="等待中…" showBar={false} />
      );

    return (
      <div className={`quick-trans-upload-cell ${selected ? "is-selected" : ""}`} onClick={onSelect}>
        {thumbSrc ? (
          <>
            <img src={thumbSrc} className="quick-trans-upload-cell-video" alt="" />
            {cellOverlay}
            <span
              className="quick-trans-upload-cell-remove"
              onClick={(e) => {
                e.stopPropagation();
                onRemove();
              }}
              title="移除"
            >
              <i className="fas fa-times" />
            </span>
          </>
        ) : item.previewUrl ? (
          <>
            <video
              className="quick-trans-upload-cell-video"
              src={item.previewUrl}
              muted
              playsInline
              preload={selected ? "metadata" : "none"}
              onLoadedMetadata={(e) => {
                try {
                  const el = e.currentTarget;
                  if (el.readyState >= 1) el.currentTime = Math.min(0.05, (el.duration || 1) * 0.001 || 0.05);
                } catch {}
              }}
            />
            {cellOverlay}
            <span
              className="quick-trans-upload-cell-remove"
              onClick={(e) => {
                e.stopPropagation();
                onRemove();
              }}
              title="移除"
            >
              <i className="fas fa-times" />
            </span>
          </>
        ) : (
          <>
            <div className="quick-trans-cell-placeholder">+</div>
            {cellOverlay}
            <span
              className="quick-trans-upload-cell-remove"
              onClick={(e) => {
                e.stopPropagation();
                onRemove();
              }}
              title="移除"
            >
              <i className="fas fa-times" />
            </span>
          </>
        )}
      </div>
    );
  },
  (prev, next) =>
    prev.item.id === next.item.id &&
    prev.selected === next.selected &&
    (prev.item.status ?? "queued") === (next.item.status ?? "queued") &&
    prev.item.progress === next.item.progress &&
    prev.item.coverPreviewUrl === next.item.coverPreviewUrl &&
    prev.item.coverUrl === next.item.coverUrl &&
    prev.item.previewUrl === next.item.previewUrl &&
    prev.item.error === next.item.error
);

export interface QuickTransCreateSeriesModalProps {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
  showError: (msg: string) => void;
  showSuccess: (msg: string) => void;
  showWarning: (msg: string) => void;
}

export const QuickTransCreateSeriesModal: FC<QuickTransCreateSeriesModalProps> = ({
  open,
  onClose,
  onCreated,
  showError,
  showSuccess,
  showWarning,
}) => {
  const hasPoints = useHasPermission(PERMISSIONS.POINTS_VIEW) === true;
  const [episodeName, setEpisodeName] = useState("");
  const [sourceLanguage, setSourceLanguage] = useState("中文");
  const [subtitleExtractMode, setSubtitleExtractMode] = useState<"video" | "audio">("video");
  const [targetLanguageCodes, setTargetLanguageCodes] = useState<string[]>([]);
  const [quickTransTargetLanguages, setQuickTransTargetLanguages] = useState<Record<string, string>>({});
  const [quickTransTargetLangLoading, setQuickTransTargetLangLoading] = useState(false);
  const [uploadItems, setUploadItems] = useState<QuickTransUploadItem[]>([]);
  const [selectedVideoIndex, setSelectedVideoIndex] = useState(-1);
  const [uploadBatchNo, setUploadBatchNo] = useState<string>("");
  const uploadBatchNoRef = useRef<string>("");
  const closingCleanupRef = useRef(false);
  const uploadItemsRef = useRef<QuickTransUploadItem[]>([]);
  const previousUploadItemsRef = useRef<QuickTransUploadItem[]>([]);
  const pendingFilesRef = useRef<File[]>([]);
  const flushTimerRef = useRef<number | null>(null);
  const uploadPipelineRef = useRef<Promise<void>>(Promise.resolve());
  const uploadProgressLastEmitRef = useRef<Map<string, number>>(new Map());
  const getBatchNoMutexRef = useRef<Promise<any> | null>(null);
  const videoMetadataChainRef = useRef<Promise<unknown>>(Promise.resolve());
  const pointsRefreshTimerRef = useRef<number | null>(null);
  const [creating, setCreating] = useState(false);
  const [createPreviewEstimatedPoint, setCreatePreviewEstimatedPoint] = useState<number | null>(null);
  const createPreviewReqIdRef = useRef(0);
  /** 刚打开弹窗不请求 point-preview；有上传/删视频、选/取消语言、改字幕方式后再请求 */
  const skipPointPreviewUntilInteractionRef = useRef(false);

  /** 仅当 id / duration 集合变化时变，避免上传进度刷屏打 point-preview */
  const createPointPreviewVideosKey = useMemo(
    () =>
      uploadItems
        .map((u) => {
          const d = u.duration;
          const dur = typeof d === "number" && Number.isFinite(d) && d > 0 ? d.toFixed(4) : "";
          return `${u.id}:${dur}`;
        })
        .sort()
        .join("|"),
    [uploadItems]
  );

  const createPointPreviewTargetLangKey = useMemo(
    () => [...targetLanguageCodes].sort().join(","),
    [targetLanguageCodes]
  );

  const progressSummary = useMemo(() => {
    const total = uploadItems.length;
    const done = uploadItems.filter((x) => x.status === "done").length;
    return { done, total };
  }, [uploadItems]);

  const canSubmitAllVideos = useMemo(
    () => uploadItems.length > 0 && uploadItems.every((x) => x.status === "done"),
    [uploadItems]
  );

  const selectedUploadItem = useMemo(() => {
    if (selectedVideoIndex < 0 || selectedVideoIndex >= uploadItems.length) return undefined;
    return uploadItems[selectedVideoIndex];
  }, [uploadItems, selectedVideoIndex]);

  const sourceLanguageCode = useMemo(
    () => String(getLanguageCodeFromName(sourceLanguage) || "").trim(),
    [sourceLanguage]
  );

  useEffect(() => {
    if (!sourceLanguageCode) return;
    setTargetLanguageCodes((prev) =>
      prev.filter((c) => String(c || "").trim() !== sourceLanguageCode)
    );
  }, [sourceLanguageCode]);

  useEffect(() => {
    uploadBatchNoRef.current = uploadBatchNo || "";
  }, [uploadBatchNo]);

  useEffect(() => {
    uploadItemsRef.current = uploadItems;
  }, [uploadItems]);

  useEffect(() => {
    const previousItems = previousUploadItemsRef.current;
    const currentIds = new Set(uploadItems.map((item) => item.id));
    previousItems.forEach((item) => {
      if (currentIds.has(item.id)) return;
      try {
        if (item.previewUrl) URL.revokeObjectURL(item.previewUrl);
        if (item.coverPreviewUrl) URL.revokeObjectURL(item.coverPreviewUrl);
      } catch {}
    });
    previousUploadItemsRef.current = uploadItems;
  }, [uploadItems]);

  useEffect(() => {
    return () => {
      if (pointsRefreshTimerRef.current != null) {
        window.clearInterval(pointsRefreshTimerRef.current);
        pointsRefreshTimerRef.current = null;
      }
      uploadItemsRef.current.forEach((it) => {
        try {
          if (it.previewUrl) URL.revokeObjectURL(it.previewUrl);
          if (it.coverPreviewUrl) URL.revokeObjectURL(it.coverPreviewUrl);
        } catch {}
      });
    };
  }, []);

  useEffect(() => {
    if (!open) return;
    uploadPipelineRef.current = Promise.resolve();
    getBatchNoMutexRef.current = null;
    setEpisodeName("");
    setSourceLanguage("中文");
    setSubtitleExtractMode("video");
    setTargetLanguageCodes([]);
    setUploadItems([]);
    setSelectedVideoIndex(-1);
    setUploadBatchNo("");
    uploadBatchNoRef.current = "";
    setQuickTransTargetLanguages({});
    setQuickTransTargetLangLoading(true);
    skipPointPreviewUntilInteractionRef.current = true;
  }, [open]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      setQuickTransTargetLangLoading(true);
      try {
        const res = await getDictDataListSimpleApi("erp_language");
        if (cancelled) return;
        if (res.code === 0 && Array.isArray(res.data)) {
          const record: Record<string, string> = {};
          for (const row of res.data) {
            const code = String(row.value ?? "").trim();
            const label = String(row.name ?? "").trim();
            if (!code || !label) continue;
            if (!(code in record)) record[code] = label;
          }
          setQuickTransTargetLanguages(record);
        } else {
          setQuickTransTargetLanguages({});
          showError(res.msg || "获取目标语言列表失败");
        }
      } catch (e: any) {
        if (!cancelled) {
          setQuickTransTargetLanguages({});
          showError(e?.msg || e?.message || "获取目标语言列表失败");
        }
      } finally {
        if (!cancelled) setQuickTransTargetLangLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, showError]);

  useEffect(() => {
    if (!open) {
      createPreviewReqIdRef.current += 1;
      setCreatePreviewEstimatedPoint(null);
      return;
    }

    if (skipPointPreviewUntilInteractionRef.current) {
      skipPointPreviewUntilInteractionRef.current = false;
      createPreviewReqIdRef.current += 1;
      setCreatePreviewEstimatedPoint(null);
      return;
    }

    const videos = uploadItems
      .filter((x) => typeof x.duration === "number" && Number.isFinite(x.duration) && x.duration > 0)
      .map((x) => ({ duration: x.duration as number }));

    /** 缺目标语或尚无有效 duration 的视频时不请求，避免接口报错、仅显示「—」 */
    if (targetLanguageCodes.length === 0 || videos.length === 0) {
      createPreviewReqIdRef.current += 1;
      setCreatePreviewEstimatedPoint(null);
      return;
    }

    const reqId = ++createPreviewReqIdRef.current;
    let cancelled = false;

    const subtitleExtractType = subtitleExtractMode === "audio" ? "audio" : "vision";

    void (async () => {
      try {
        const res = await postTaskPointPreviewApi({
          priceType: "short_drama_tencent_mps",
          subtitleExtractType,
          targetLangCodes: targetLanguageCodes,
          videos,
        });
        if (cancelled || reqId !== createPreviewReqIdRef.current) return;
        if (res?.code === 0 && res.data?.estimatedPoint != null) {
          setCreatePreviewEstimatedPoint(res.data.estimatedPoint);
        } else {
          setCreatePreviewEstimatedPoint(null);
        }
      } catch {
        if (!cancelled && reqId === createPreviewReqIdRef.current) {
          setCreatePreviewEstimatedPoint(null);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open, subtitleExtractMode, createPointPreviewTargetLangKey, createPointPreviewVideosKey]);

  const enqueueFiles = (files: File[]) => {
    const maxNew = Math.max(0, MAX_UPLOAD_VIDEO_EPISODES - uploadItemsRef.current.length);
    if (maxNew <= 0) {
      showWarning(`视频最多上传 ${MAX_UPLOAD_VIDEO_EPISODES} 集`);
      return [];
    }
    const take = files.slice(0, maxNew);
    if (take.length < files.length) {
      showWarning(`单次最多还能添加 ${maxNew} 集，已为您截取前面的文件`);
    }
    const now = Date.now();
    const newItems: QuickTransUploadItem[] = take.map((file, idx) => {
      const id =
        typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
          ? crypto.randomUUID()
          : `${now}-${idx}-${Math.random().toString(16).slice(2)}`;
      return {
        id,
        sourceFileName: file.name,
        file,
        previewUrl: URL.createObjectURL(file),
        status: "queued",
        progress: 0,
      };
    });
    setUploadItems((prev) => {
      const next = [...prev, ...newItems];
      if (prev.length === 0 && newItems.length > 0) setSelectedVideoIndex(0);
      return next;
    });
    return newItems;
  };

  const startUploadBatch = useCallback(async (batchItems: QuickTransUploadItem[]) => {
    if (!batchItems.length) return;

    const tryEmitUploadProgress = (id: string, percent: number) => {
      const now = Date.now();
      const last = uploadProgressLastEmitRef.current.get(id) ?? 0;
      if (percent < 100 && now - last < UPLOAD_PROGRESS_UI_MS) return;
      uploadProgressLastEmitRef.current.set(id, now);
      setUploadItems((prev) => prev.map((x) => (x.id === id ? { ...x, progress: percent } : x)));
    };

    const processOneEpisode = async (it: QuickTransUploadItem) => {
      try {
        const resolveVideoFile = () =>
          uploadItemsRef.current.find((u) => u.id === it.id)?.file ?? it.file;

        let videoFile = resolveVideoFile();
        if (!videoFile) {
          setUploadItems((prev) =>
            prev.map((x) =>
              x.id === it.id
                ? { ...x, status: "error", error: "文件引用已失效，请移除后重新添加", progress: 0 }
                : x
            )
          );
          return;
        }

        uploadProgressLastEmitRef.current.delete(it.id);

        setUploadItems((prev) =>
          prev.map((x) => (x.id === it.id ? { ...x, status: "preparing", error: undefined } : x))
        );

        let coverFile: File;
        let duration: number;
        let coverFileName: string;

        try {
          const fileForMetadata = videoFile;
          const metaPromise = videoMetadataChainRef.current.then(() => getVideoMetadata(fileForMetadata));
          videoMetadataChainRef.current = metaPromise.then(
            () => undefined,
            () => undefined
          );
          const meta = await metaPromise;
          duration = meta.duration;
          coverFileName = meta.coverFileName;
          coverFile = new File([meta.coverBlob], meta.coverFileName, {
            type: meta.coverBlob.type || "image/jpeg",
          });
          const coverPreviewUrlNew = URL.createObjectURL(meta.coverBlob);

          setUploadItems((prev) =>
            prev.map((x) =>
              x.id === it.id
                ? {
                    ...x,
                    status: "preparing",
                    duration,
                    coverFileName,
                    coverPreviewUrl: coverPreviewUrlNew,
                  }
                : x
            )
          );
          await yieldToPaint();
        } catch (e: any) {
          setUploadItems((prev) =>
            prev.map((x) =>
              x.id === it.id
                ? { ...x, status: "error", error: e?.message || "读取视频信息失败", progress: 0 }
                : x
            )
          );
          return;
        }

        try {
          videoFile = resolveVideoFile();
          if (!videoFile) {
            setUploadItems((prev) =>
              prev.map((x) =>
                x.id === it.id
                  ? { ...x, status: "error", error: "文件引用已失效，请移除后重新添加", progress: 0 }
                  : x
              )
            );
            return;
          }

          let presignedResp: FastTranslateSeries.GetPresignedUrlsResponse;
          const fetchSignature = async () => {
            return await getFastTranslatePresignedUrlsApi({
              uploadBatchNo: uploadBatchNoRef.current || undefined,
              files: [{ fileName: it.sourceFileName, coverFileName }],
            });
          };

          if (!uploadBatchNoRef.current) {
            if (!getBatchNoMutexRef.current) {
              getBatchNoMutexRef.current = (async () => {
                const resp = await fetchSignature();
                const bNo = resp.data?.uploadBatchNo;
                if (bNo) {
                  uploadBatchNoRef.current = bNo;
                  setUploadBatchNo(bNo);
                }
                return resp;
              })();
              presignedResp = await getBatchNoMutexRef.current;
            } else {
              await getBatchNoMutexRef.current;
              presignedResp = await fetchSignature();
            }
          } else {
            presignedResp = await fetchSignature();
          }

          const batchNo = presignedResp.data?.uploadBatchNo || uploadBatchNoRef.current;
          if (batchNo && batchNo !== uploadBatchNoRef.current) {
            uploadBatchNoRef.current = batchNo;
            setUploadBatchNo(batchNo);
          }

          const sign =
            presignedResp.data?.files?.find((f) => f.sourceFileName === it.sourceFileName) ??
            presignedResp.data?.files?.[0];
          if (!sign) {
            setUploadItems((prev) =>
              prev.map((x) => (x.id === it.id ? { ...x, status: "error", error: "未获取到预签名信息" } : x))
            );
            return;
          }

          if (!batchNo) {
            setUploadItems((prev) =>
              prev.map((x) => (x.id === it.id ? { ...x, status: "error", error: "缺少上传批次号" } : x))
            );
            return;
          }

          setUploadItems((prev) =>
            prev.map((x) =>
              x.id === it.id
                ? {
                    ...x,
                    status: "uploading",
                    sourceSignature: sign.sourceSignature,
                    coverSignature: sign.coverSignature,
                    sourcePath: sign.sourcePath,
                    coverPath: sign.coverPath,
                    progress: 0,
                  }
                : x
            )
          );

          await uploadFileToTosApi(videoFile, sign.sourceSignature, sign.sourcePath, (p) => {
            tryEmitUploadProgress(it.id, p);
          });
          await uploadFileToTosApi(coverFile, sign.coverSignature, sign.coverPath);

          setUploadItems((prev) => prev.map((x) => (x.id === it.id ? { ...x, status: "confirming" } : x)));

          const confirmResp = await confirmFastTranslateUploadApi({
            uploadBatchNo: batchNo,
            files: [
              {
                fileName: it.sourceFileName,
                filePath: sign.sourcePath,
                coverFileName,
                coverFilePath: sign.coverPath,
                duration,
              },
            ],
          });

          const c =
            confirmResp.data?.files?.find((f) => f.sourceFileName === it.sourceFileName) ??
            confirmResp.data?.files?.[0];
          if (!c) {
            setUploadItems((prev) =>
              prev.map((x) => (x.id === it.id ? { ...x, status: "error", error: "确认上传无返回" } : x))
            );
            return;
          }

          setUploadItems((prev) =>
            prev.map((x) => {
              if (x.id !== it.id) return x;
              try {
                if (x.previewUrl) URL.revokeObjectURL(x.previewUrl);
                if (x.coverPreviewUrl) URL.revokeObjectURL(x.coverPreviewUrl);
              } catch {}
              return {
                ...x,
                status: "done",
                progress: 100,
                file: null,
                previewUrl: undefined,
                coverPreviewUrl: undefined,
                episodeName: c.episodeName,
                episodeNo: c.episodeNo,
                tosUrl: c.tosUrl,
                tosKey: c.tosKey,
                coverUrl: c.coverUrl,
              };
            })
          );
        } catch (e: any) {
          setUploadItems((prev) =>
            prev.map((x) =>
              x.id === it.id
                ? { ...x, status: "error", error: e?.message || "上传失败", progress: Math.min(x.progress || 0, 99) }
                : x
            )
          );
        }
      } finally {
        uploadProgressLastEmitRef.current.delete(it.id);
      }
    };

    const executing = new Set<Promise<void>>();
    for (const it of batchItems) {
      const p = processOneEpisode(it).finally(() => {
        executing.delete(p);
      });
      executing.add(p);
      if (executing.size >= QUICK_TRANS_UPLOAD_CONCURRENCY) {
        await Promise.race(executing);
      }
    }
    await Promise.all(Array.from(executing));
  }, []);

  const flushPendingFiles = () => {
    const files = pendingFilesRef.current;
    pendingFilesRef.current = [];
    if (!files.length) return;
    const newItems = enqueueFiles(files);
    uploadPipelineRef.current = uploadPipelineRef.current.catch(() => {}).then(async () => {
      await startUploadBatch(newItems);
    });
  };

  const scheduleFlush = () => {
    if (flushTimerRef.current) return;
    flushTimerRef.current = window.setTimeout(() => {
      flushTimerRef.current = null;
      flushPendingFiles();
    }, 0);
  };

  const removeUploadFile = async (index: number) => {
    const target = uploadItems[index];
    const batchNo = uploadBatchNoRef.current;
    const filePath = target?.sourcePath || "";
    const coverFilePath = target?.coverPath || "";

    if (target && batchNo && filePath && coverFilePath) {
      try {
        await deleteFastTranslateUploadItemApi({
          uploadBatchNo: batchNo,
          filePath,
          coverFilePath,
        });
      } catch (e: any) {
        showError(e?.msg || e?.message || "移除临时上传项失败");
      }
    }

    setUploadItems((prev) => {
      const it = prev[index];
      if (it?.previewUrl) URL.revokeObjectURL(it.previewUrl);
      if (it?.coverPreviewUrl) URL.revokeObjectURL(it.coverPreviewUrl);
      return prev.filter((_, i) => i !== index);
    });
    setSelectedVideoIndex((prevIdx) => {
      if (prevIdx !== index) return prevIdx > index ? prevIdx - 1 : prevIdx;
      const newLen = uploadItems.length - 1;
      return newLen === 0 ? -1 : Math.min(index, newLen - 1);
    });
  };

  const closeCreateModal = async (options?: { skipBatchCleanup?: boolean }) => {
    const skipBatchCleanup = !!options?.skipBatchCleanup;
    const currentBatchNo = uploadBatchNoRef.current;

    if (!skipBatchCleanup && currentBatchNo && !closingCleanupRef.current) {
      closingCleanupRef.current = true;
      try {
        await deleteFastTranslateUploadBatchApi({
          uploadBatchNos: [currentBatchNo],
        });
      } catch (e: any) {
        showError(e?.msg || e?.message || "清理临时上传资源失败");
      } finally {
        closingCleanupRef.current = false;
      }
    }

    setUploadBatchNo("");
    uploadBatchNoRef.current = "";
    onClose();
  };

  const handleCreateConfirm = async () => {
    if (creating) return;
    const name = (episodeName || "").trim();
    if (!name) {
      showWarning("请先填写剧名");
      return;
    }
    if (!targetLanguageCodes.length) {
      showWarning("请先选择目标语言");
      return;
    }

    const readyVideos = uploadItems
      .filter((x) => x.status === "done" && x.tosUrl && x.tosKey && x.coverUrl && typeof x.duration === "number")
      .slice()
      .sort((a, b) => (a.episodeNo || 0) - (b.episodeNo || 0));

    if (readyVideos.length === 0) {
      showWarning("请先上传视频");
      return;
    }

    const sourceLangCode = getLanguageCodeFromName(sourceLanguage);
    const subtitleExtractType: FastTranslateSeries.SubtitleExtractType =
      subtitleExtractMode === "audio" ? "audio" : "vision";
    const needRemovalSubtitle = subtitleExtractMode === "video";

    const triggerPointsRefreshBurst = () => {
      window.dispatchEvent(new Event("points:refresh"));
      triggerMembershipRefresh();
      if (pointsRefreshTimerRef.current != null) {
        window.clearInterval(pointsRefreshTimerRef.current);
      }
      let elapsed = 0;
      pointsRefreshTimerRef.current = window.setInterval(() => {
        elapsed += QUICK_TRANS_POINTS_REFRESH_POLL_MS;
        window.dispatchEvent(new Event("points:refresh"));
        if (elapsed >= QUICK_TRANS_POINTS_REFRESH_TOTAL_MS) {
          if (pointsRefreshTimerRef.current != null) {
            window.clearInterval(pointsRefreshTimerRef.current);
            pointsRefreshTimerRef.current = null;
          }
        }
      }, QUICK_TRANS_POINTS_REFRESH_POLL_MS);
    };

    try {
      setCreating(true);
      await createFastTranslateSeriesApi({
        name,
        sourceLangCode,
        needRemovalSubtitle,
        subtitleExtractType,
        extJson: JSON.stringify({ cover: readyVideos[0].coverUrl }),
        targetLangCodes: JSON.stringify(targetLanguageCodes),
        videos: readyVideos.map((v) => ({
          sourceFileName: v.sourceFileName,
          episodeName: v.episodeName || v.sourceFileName.replace(/\.[^/.]+$/, ""),
          episodeNo: v.episodeNo || 0,
          tosUrl: v.tosUrl!,
          tosKey: v.tosKey!,
          coverUrl: v.coverUrl!,
          duration: v.duration!,
          extJson: JSON.stringify({ poster: v.coverUrl }),
        })),
      });
      showSuccess("创建成功");
      triggerPointsRefreshBurst();
      await closeCreateModal({ skipBatchCleanup: true });
      onCreated();
    } catch (e: any) {
      showError(e?.msg || e?.message || "创建失败");
    } finally {
      setCreating(false);
    }
  };

  return (
    <AutoModel
      title="AI 速译"
      open={open}
      onClose={() => {
        void closeCreateModal();
      }}
      maskClosable={false}
      keyboard={false}
      contentMaxWidth={980}
      contentClassName="quick-trans-create-modal"
      overlayClassName="quick-trans-modal-overlay"
    >
      <div className="quick-trans-create-modal-body">
        <div className="quick-trans-create-left">
          <Upload
            accept="video/*"
            showUploadList={false}
            beforeUpload={(file) => {
              const nameCheck = validateVideoUploadFileName(file.name);
              if (!nameCheck.valid) {
                showError(nameCheck.message || getVideoUploadFileNameInvalidMessage(getVideoUploadBaseName(file.name)));
                return false;
              }
              const n = uploadItemsRef.current.length + pendingFilesRef.current.length;
              if (n >= MAX_UPLOAD_VIDEO_EPISODES) {
                showWarning(`视频最多上传 ${MAX_UPLOAD_VIDEO_EPISODES} 集`);
                return false;
              }
              pendingFilesRef.current.push(file);
              scheduleFlush();
              return false;
            }}
            multiple
          >
            <Button type="primary" className="quick-trans-upload-btn">
              上传视频
              <span className="quick-trans-upload-btn-hint">（可多次上传）</span>
            </Button>
          </Upload>
          <div className="quick-trans-video-count">
            上传进度 {progressSummary.done}/{progressSummary.total}
          </div>
          <div className="quick-trans-left-scroll">
            {uploadItems.length === 0 ? null : (
              <div className="quick-trans-upload-grid">
                {uploadItems.map((it, i) => (
                  <QuickTransUploadGridCell
                    key={it.id}
                    item={it}
                    selected={selectedVideoIndex === i}
                    onSelect={() => setSelectedVideoIndex(i)}
                    onRemove={() => {
                      const idx = uploadItemsRef.current.findIndex((x) => x.id === it.id);
                      if (idx !== -1) void removeUploadFile(idx);
                    }}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
        <div className="quick-trans-create-center">
          <VideoShow
            title={
              selectedUploadItem
                ? selectedUploadItem.episodeName ||
                  selectedUploadItem.sourceFileName.replace(/\.[^/.]+$/, "")
                : undefined
            }
            videoSrc={
              selectedUploadItem?.previewUrl ??
              (selectedUploadItem?.status === "done" && selectedUploadItem.tosUrl
                ? selectedUploadItem.tosUrl
                : undefined)
            }
            poster={selectedUploadItem?.coverUrl ?? undefined}
          />
        </div>
        <div className="quick-trans-create-right">
          <div className="quick-trans-create-right-content">
            <div className="form-item form-item-row">
              <label>剧名</label>
              <Input
                value={episodeName}
                onChange={(e) => setEpisodeName(e.target.value)}
                placeholder="请输入剧集名称"
              />
            </div>
            <div className="form-item form-item-row">
              <label>源语言</label>
              <Select
                value={sourceLanguage}
                onChange={setSourceLanguage}
                placeholder="请选择源语言"
                style={{ flex: 1, minWidth: 0 }}
                dropdownStyle={{ zIndex: 1001 }}
                popupClassName="quick-trans-create-modal__source-lang-dropdown"
                getPopupContainer={(triggerNode) => triggerNode.parentNode as HTMLElement}
              >
                {getSourceLanguageOptions().map((opt) => (
                  <Select.Option key={opt.value} value={opt.value}>
                    {opt.label}
                  </Select.Option>
                ))}
              </Select>
            </div>
            <div className="form-item form-item-subtitle-mode">
              <label>字幕提取方式</label>
              <Radio.Group
                optionType="button"
                value={subtitleExtractMode}
                onChange={(e) => setSubtitleExtractMode(e.target.value)}
                className="quick-trans-subtitle-mode-btns"
              >
                <Radio.Button value="video">画面识别</Radio.Button>
                <Radio.Button value="audio">音频识别</Radio.Button>
              </Radio.Group>
              <div className="quick-trans-subtitle-mode-hint">
                <div>若您上传的视频含有中文字幕，请选择画面识别，默认擦除中文字幕；</div>
                <div>若您上传的视频无中文字幕，请选择音频识别，默认不擦除中文字幕。</div>
              </div>
            </div>
            {quickTransTargetLangLoading ? (
              <div className="quick-trans-target-language quick-trans-target-language--loading">
                <div className="target-language-selector-label">目标语言</div>
                <div style={{ display: "flex", justifyContent: "center", padding: "24px 0" }}>
                  <Spin />
                </div>
              </div>
            ) : (
              <TargetLanguageSelector
                label="目标语言"
                value={targetLanguageCodes}
                onChange={setTargetLanguageCodes}
                maxCount={5}
                className="quick-trans-target-language"
                languages={quickTransTargetLanguages}
                disabledCodes={sourceLanguageCode ? [sourceLanguageCode] : []}
              />
            )}
            {hasPoints ? (
              <div className="quick-trans-point-estimate" title="以实际扣减为准">
                <CoinsEstimateIcon />
                <span className="quick-trans-point-estimate-label">
                  预计消耗 {createPreviewEstimatedPoint ?? 0} 积分
                </span>
              </div>
            ) : null}
          </div>
          <Button
            type="primary"
            className="quick-trans-create-confirm-btn"
            onClick={() => void handleCreateConfirm()}
            loading={creating}
            disabled={creating || !canSubmitAllVideos}
            block
          >
            确定
          </Button>
        </div>
      </div>
    </AutoModel>
  );
};
